import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'

import type { ExpenseCategoryRule } from '@/lib/api/expense-rule-api'
import type { ExpenseRuleFormValues } from '@/components/expense-rules/expense-rule-form'
import { ExpenseRuleForm } from '@/components/expense-rules/expense-rule-form'
import { ExpenseRuleLeftoverPanel } from '@/components/expense-rules/expense-rule-leftover-panel'
import { ExpenseRuleList } from '@/components/expense-rules/expense-rule-list'
import { ExpenseRulePreview } from '@/components/expense-rules/expense-rule-preview'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useApplyExpenseRules,
    useCreateExpenseRule,
    useDeleteExpenseRule,
    useExpenseRules,
    useReorderExpenseRules,
    useSimulateExpenseRulesQuery,
    useUpdateExpenseRule,
} from '@/hooks/use-expense-rule-query'
import { m } from '@/lib/paraglide/messages'
import { normalizeExpenseRuleSearch } from '@/lib/schemas/expense-rule-search'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/expense-rules',
)({
    component: ExpenseRulesPage,
    validateSearch: normalizeExpenseRuleSearch,
})

function ExpenseRulesPage() {
    const { hasPermission } = useAuth()
    const canManage = hasPermission('expense_rules.manage')
    const prefillSearch = Route.useSearch()
    const { data } = useExpenseRules()
    const rules = useMemo(
        () => [...(data ?? [])].sort((a, b) => a.priority - b.priority),
        [data],
    )

    const [editing, setEditing] = useState<ExpenseCategoryRule | null>(null)
    // "Tạo quy tắc từ giao dịch này" lands here with a prefill in the URL —
    // open the create dialog right away instead of making the user click
    // "Thêm quy tắc" again. The "Còn chưa phân loại" panel below is on this
    // SAME page, so it sets this local override instead of a URL round trip.
    const [open, setOpen] = useState(Boolean(prefillSearch.prefill_keyword))
    // Same shared patch shape as the empty-state example cards (see
    // expense-rule-examples.tsx) — the leftover panel's "Tạo quy tắc" click
    // is just another way to prefill the same form.
    const [manualPrefill, setManualPrefill] =
        useState<Partial<ExpenseRuleFormValues> | null>(null)
    // Draft list-editor state: reorder/toggle change this local copy first,
    // never the server, until "Lưu" — see plan's "draft list editor".
    const [localOrder, setLocalOrder] = useState<string[] | null>(null)
    const [localActive, setLocalActive] = useState<Record<string, boolean>>({})

    const orderedIds = localOrder ?? rules.map((r) => r.id)
    const isDirty = localOrder !== null || Object.keys(localActive).length > 0
    const overrides = orderedIds.map((id, index) => ({
        rule_id: id,
        is_active:
            localActive[id] ??
            rules.find((r) => r.id === id)?.is_active ??
            true,
        priority: index,
    }))

    const debouncedOverrides = useDebounced(overrides, 400)
    // Always enabled — even with zero rules, the leftover panel needs a real
    // simulate result (every bank transaction is unmatched) rather than the
    // empty-array default, which would otherwise misreport "mọi giao dịch
    // đều đã có quy tắc" while nothing has ever run.
    const { data: preview, isFetching } = useSimulateExpenseRulesQuery({
        overrides: debouncedOverrides,
    })

    const deleteMut = useDeleteExpenseRule()
    const reorderMut = useReorderExpenseRules()
    const applyMut = useApplyExpenseRules()

    function move(rule: ExpenseCategoryRule, direction: 'up' | 'down') {
        const ids = [...orderedIds]
        const from = ids.indexOf(rule.id)
        const to = direction === 'up' ? from - 1 : from + 1
        if (to < 0 || to >= ids.length) return
        ;[ids[from], ids[to]] = [ids[to], ids[from]]
        setLocalOrder(ids)
    }

    function toggle(rule: ExpenseCategoryRule, isActive: boolean) {
        setLocalActive((prev) => ({ ...prev, [rule.id]: isActive }))
    }

    function discard() {
        setLocalOrder(null)
        setLocalActive({})
    }

    // reorder persists BOTH the draft's new order and every rule's is_active
    // toggle in one call — passing only ids would silently drop an unsaved
    // on/off change (see useReorderExpenseRules).
    const reorderItems = overrides.map((o) => ({
        id: o.rule_id,
        is_active: o.is_active,
    }))

    async function saveOnly() {
        await reorderMut.mutateAsync(reorderItems)
        discard()
    }

    async function saveAndApply() {
        await reorderMut.mutateAsync(reorderItems)
        await applyMut.mutateAsync({ trigger: 'save_and_apply' })
        discard()
    }

    const changeCount = preview?.diff.length ?? 0

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.expense_rule_page_title()}
                    </h1>
                </div>
                {canManage && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.expense_rule_new()}
                    </Button>
                )}
            </div>

            <p className="text-sm text-muted-foreground max-w-2xl">
                {m.expense_rule_page_intro()}
            </p>

            <ExpenseRuleList
                rules={orderedIds
                    .map((id) => rules.find((r) => r.id === id))
                    .filter((r): r is ExpenseCategoryRule => Boolean(r))
                    .map((r) => ({
                        ...r,
                        is_active: localActive[r.id] ?? r.is_active,
                    }))}
                onEdit={(rule) => {
                    setEditing(rule)
                    setOpen(true)
                }}
                onDelete={(rule) => {
                    if (window.confirm(m.expense_rule_delete_confirm())) {
                        deleteMut.mutate(rule.id)
                    }
                }}
                onToggle={toggle}
                onMove={move}
                onPickExample={(patch) => {
                    setEditing(null)
                    setManualPrefill(patch)
                    setOpen(true)
                }}
            />

            <div className="rounded-md border p-3">
                <h2 className="mb-2 text-sm font-semibold">
                    {m.expense_rule_preview_title()}
                </h2>
                <ExpenseRulePreview
                    result={preview ?? null}
                    loading={isFetching}
                />
            </div>

            <div className="rounded-md border p-3">
                <h2 className="mb-2 text-sm font-semibold">
                    {m.expense_rule_leftover_title()}
                </h2>
                <ExpenseRuleLeftoverPanel
                    groups={preview?.leftover ?? []}
                    loading={!preview}
                    onCreateRule={(prefill) => {
                        setEditing(null)
                        setManualPrefill(prefill)
                        setOpen(true)
                    }}
                />
            </div>

            {isDirty && canManage && (
                <div className="sticky bottom-4 flex flex-wrap items-center gap-2 rounded-md border bg-background p-3 shadow-lg">
                    <span className="text-sm font-medium">
                        {m.expense_rule_unsaved_changes({
                            count: String(
                                Object.keys(localActive).length +
                                    (localOrder ? 1 : 0),
                            ),
                        })}
                    </span>
                    <Button
                        size="sm"
                        onClick={saveAndApply}
                        disabled={applyMut.isPending || reorderMut.isPending}
                    >
                        {m.expense_rule_save_and_apply({
                            count: String(changeCount),
                        })}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={saveOnly}
                        disabled={reorderMut.isPending}
                    >
                        {m.expense_rule_save_only()}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={discard}>
                        {m.expense_rule_discard_changes()}
                    </Button>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editing
                                ? m.expense_rule_edit()
                                : m.expense_rule_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <RuleFormWithPreview
                        editing={editing}
                        prefill={
                            !editing && manualPrefill
                                ? manualPrefill
                                : !editing && prefillSearch.prefill_keyword
                                  ? {
                                        match_field:
                                            prefillSearch.prefill_field ??
                                            'counterparty',
                                        keywords: [
                                            prefillSearch.prefill_keyword,
                                        ],
                                        category_id:
                                            prefillSearch.prefill_category_id,
                                    }
                                  : undefined
                        }
                        onDone={() => {
                            setOpen(false)
                            setManualPrefill(null)
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function RuleFormWithPreview({
    editing,
    prefill,
    onDone,
}: {
    editing: ExpenseCategoryRule | null
    prefill?: Partial<ExpenseRuleFormValues>
    onDone: () => void
}) {
    const [draft, setDraft] = useState<ExpenseRuleFormValues | null>(null)
    const debouncedDraft = useDebounced(draft, 400)
    const { data: preview, isFetching } = useSimulateExpenseRulesQuery(
        debouncedDraft
            ? {
                  draft: {
                      id: editing?.id,
                      ...debouncedDraft,
                      min_amount: debouncedDraft.min_amount ?? undefined,
                      max_amount: debouncedDraft.max_amount ?? undefined,
                      vendor_name: debouncedDraft.vendor_name ?? undefined,
                  },
              }
            : {},
        Boolean(debouncedDraft && debouncedDraft.keywords.length > 0),
    )

    const createMut = useCreateExpenseRule()
    const updateMut = useUpdateExpenseRule(editing?.id ?? '')
    const applyMut = useApplyExpenseRules()
    const saveMut = editing ? updateMut : createMut
    const [applying, setApplying] = useState(false)

    const matchedCount =
        preview?.rules.find((r) => r.is_draft)?.matched_count ?? 0

    return (
        <div className="grid gap-4 md:grid-cols-2">
            <ExpenseRuleForm
                initial={editing}
                prefill={prefill}
                matchedCount={matchedCount}
                submitting={saveMut.isPending || applying}
                onChange={setDraft}
                onSubmit={async (values) => {
                    await saveMut.mutateAsync(values)
                    onDone()
                }}
                onSubmitAndApply={async (values) => {
                    await saveMut.mutateAsync(values)
                    setApplying(true)
                    try {
                        await applyMut.mutateAsync({
                            trigger: 'save_and_apply',
                        })
                    } finally {
                        setApplying(false)
                    }
                    onDone()
                }}
            />
            <div className="rounded-md border p-3">
                <h3 className="mb-2 text-sm font-semibold">
                    {m.expense_rule_preview_title()}
                </h3>
                <ExpenseRulePreview
                    result={preview ?? null}
                    loading={isFetching}
                />
            </div>
        </div>
    )
}
